import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Flex, SegmentedControl, Text, Tooltip } from '@mantine/core';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { SAMPLE_INVESTOR_ID } from '@dealstack/shared/lib/documents/fieldBindingCatalogue';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { getFileReactQuery } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { DocumentViewerHeader } from '@dealstack/frontend/shared/components/DocumentViewerV2/common/DocumentViewerHeader.tsx';
import { BindPanel } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanel.tsx';
import { BindPanelEmptyState } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanelEmptyState.tsx';
import { BindPanelFooter } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanelFooter.tsx';
import type { PanelHeaderEdit } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/templateNameField.types.ts';
import { DocumentWorkflowTemplateSourceFormat } from '@dealstack/shared/lib/generated';
import { DocxFlowEditor } from '@dealstack/frontend/shared/components/FieldBinding/DocxFieldBinding/DocxFlowEditor.tsx';
import type { DocxFlowEditorAdapter } from '@dealstack/frontend/shared/components/FieldBinding/DocxFieldBinding/docxFlowEditor.adapter.ts';
import { dealDocxFlowAdapter } from './dealDocxFlowAdapter.ts';
import { EditorCanvasPane } from './EditorCanvasPane.tsx';
import { PreviewAsSelect } from './PreviewAsSelect.tsx';
import { useInvestorOptions } from './useInvestorOptions.ts';
import { usePreviewInvestorShortcuts } from './usePreviewInvestorShortcuts.ts';
import {
	DEFAULT_CANVAS_SCALE,
	MAX_SCALE,
	MIN_SCALE,
	SCALE_STEP,
} from './FieldBindingEditor.types.ts';
import type { ViewMode } from './FieldBindingEditor.types.ts';
import type { FieldBindingEditorBinding } from './useFieldBinding.types.ts';
import {
	BindPanelSlot,
	CanvasPane,
	HeaderActionSlot,
	HeaderContent,
	Legend,
	LegendItem,
	LegendSwatch,
	SidePanel,
	Split,
	ToggleRow,
} from './FieldBindingEditor.styled.ts';

interface Props {
	templateId: string;
	file: Blob | string;
	/** What was uploaded. A Word template has no coordinates to draw boxes against, so it
	 *  gets the named-field surface instead. */
	sourceFormat?: DocumentWorkflowTemplateSourceFormat;
	/**
	 * Who owns this template, for the Word surface — see {@link DocxFlowEditorAdapter}.
	 * Defaults to the deal's, which is the only caller that had one before the library did.
	 */
	docxAdapter?: DocxFlowEditorAdapter;
	binding: FieldBindingEditorBinding;
	/** Resolves to the fileId of a render with values filled in, for the preview toggle. */
	requestPreview: (investorId: string | null) => Promise<{ fileId: string }>;
	/** Absent at global level: no deal, so no investors to preview as. */
	showInvestorPreview?: boolean;
	/**
	 * Rendered in the toggle row's right cell when there is no "Preview as" pill. PDF only —
	 * the Word surface has its own header and drops this, so nothing load-bearing belongs here.
	 */
	headerAction?: ReactNode;
	/** Pinned above {@link paletteTabs} at the top of the side panel, outside the per-field
	 *  panel, so it stays put while the selection changes — the template's own name. */
	panelHeader?: ReactNode;
	/** Folds an unsaved {@link panelHeader} edit into this editor's Save and Discard, so the
	 *  panel's one footer covers everything in the panel. */
	panelHeaderEdit?: PanelHeaderEdit;
	/** Pinned at the top of the side panel — the editor-wide Data points / Signatures bar. */
	paletteTabs?: ReactNode;
	/**
	 * Canvas zoom, when a host shares one across surfaces. Uncontrolled otherwise.
	 *
	 * The tabbed editor owns it so the page does not resize under the author on a tab switch;
	 * a standalone caller passes neither and keeps this editor's own default.
	 */
	scale?: number;
	onScaleChange?: (scale: number) => void;
	initialPreviewInvestorId?: string | null;
	/** Lets a caller mirror the preview selection so it can refetch investor-scoped data. */
	onPreviewInvestorChange?: (investorId: string | null) => void;
	onClose?: () => void;
}

export const FieldBindingEditor = ({
	templateId,
	file,
	sourceFormat,
	docxAdapter = dealDocxFlowAdapter,
	binding,
	requestPreview,
	showInvestorPreview,
	headerAction,
	panelHeader,
	panelHeaderEdit,
	paletteTabs,
	scale,
	onScaleChange,
	initialPreviewInvestorId,
	onPreviewInvestorChange,
	onClose,
}: Props) => {
	// A Word template has no coordinates to draw boxes against, so it gets the document itself
	// as the surface: editable prose, with fields placed by typing `{{` or dragging one in.
	if (sourceFormat === DocumentWorkflowTemplateSourceFormat.DOCX) {
		return (
			<DocxFlowEditor
				templateId={templateId}
				adapter={docxAdapter}
				panelHeader={panelHeader}
				panelHeaderEdit={panelHeaderEdit}
				onClose={onClose}
			/>
		);
	}
	return (
		<PdfFieldBindingEditor
			templateId={templateId}
			file={file}
			binding={binding}
			requestPreview={requestPreview}
			showInvestorPreview={showInvestorPreview}
			headerAction={headerAction}
			panelHeader={panelHeader}
			panelHeaderEdit={panelHeaderEdit}
			paletteTabs={paletteTabs}
			scale={scale}
			onScaleChange={onScaleChange}
			initialPreviewInvestorId={initialPreviewInvestorId}
			onPreviewInvestorChange={onPreviewInvestorChange}
			onClose={onClose}
		/>
	);
};

type PdfProps = Omit<Props, 'sourceFormat' | 'docxAdapter'>;

const PdfFieldBindingEditor = ({
	templateId,
	file,
	binding,
	requestPreview,
	showInvestorPreview,
	headerAction,
	panelHeader,
	panelHeaderEdit,
	paletteTabs,
	scale: controlledScale,
	onScaleChange,
	initialPreviewInvestorId,
	onPreviewInvestorChange,
	onClose,
}: PdfProps) => {
	const tMipManage = useTranslationMipManage();
	const [viewMode, setViewMode] = useState<ViewMode>('original');
	// Controlled when the tabbed editor shares one zoom across its surfaces; its own
	// otherwise, so a standalone caller is unaffected.
	const [ownScale, setOwnScale] = useState(DEFAULT_CANVAS_SCALE);
	const scale = controlledScale ?? ownScale;
	const setScale = onScaleChange ?? setOwnScale;
	// Default to the built-in sample investor so sample values and the preview
	// work the moment the editor opens, before any real investor is picked.
	const [selectedInvestorId, setSelectedInvestorId] = useState<string | null>(
		initialPreviewInvestorId ?? SAMPLE_INVESTOR_ID
	);

	const onUpscale = () => setScale(Math.min(MAX_SCALE, scale + SCALE_STEP));
	const onDownscale = () => setScale(Math.max(MIN_SCALE, scale - SCALE_STEP));

	// Mirrors the selection out to the caller so a deal-scoped adapter can refetch its
	// investor-scoped catalogue — this component only owns the UI selection, not that data.
	const handlePreviewInvestorChange = (investorId: string | null) => {
		setSelectedInvestorId(investorId);
		onPreviewInvestorChange?.(investorId);
	};

	// Hooks cannot be conditional: always fetch, but only let it hit the
	// network when the caller actually offers investor preview.
	const {
		options: investorOptions,
		onDropdownOpen: onInvestorDropdownOpen,
		isFetching: isFetchingInvestors,
	} = useInvestorOptions({
		enabled: showInvestorPreview ?? false,
		preload: viewMode === 'preview',
	});

	const {
		bindings,
		catalogue,
		securities,
		scopedSamples,
		uncollectedRequirements,
		isLoading,
		selectedFieldKey,
		setSelectedFieldKey,
		onChangeBinding,
		onApplySecurityToAll,
		onBoxChange,
		onAddBox,
		onRemove,
		onRenameField,
		save,
		discard,
		isDirty,
		isSaving,
		boundCount,
		totalCount,
	} = binding;

	usePreviewInvestorShortcuts({
		enabled: (showInvestorPreview ?? false) && viewMode === 'preview' && !isSaving,
		options: investorOptions,
		value: selectedInvestorId,
		onChange: handlePreviewInvestorChange,
	});

	// Removing a field also clears the selection so the BindPanel closes
	// rather than pointing at a field that no longer exists.
	const handleRemove = (fieldKey: string) => {
		onRemove(fieldKey);
		setSelectedFieldKey(null);
	};

	// One footer for the whole panel: the bindings below it and whatever the header holds are
	// both unsaved work in the same column, so they save and discard together.
	const isPanelDirty = isDirty || !!panelHeaderEdit?.isDirty;
	const handlePanelSave = async () => {
		try {
			await Promise.all([
				isDirty ? save() : undefined,
				panelHeaderEdit?.isDirty ? panelHeaderEdit.save() : undefined,
			]);
		} catch {
			// Each write reports its own failure; nothing here depends on the outcome, and an
			// unhandled rejection is all a rethrow would add.
		}
	};
	const handlePanelDiscard = () => {
		discard();
		panelHeaderEdit?.discard();
	};

	const previewMutation = useMutation({
		mutationFn: (investorId: string | null) => requestPreview(investorId),
	});

	// Re-run the preview generation whenever preview mode is entered (or the
	// investor changes while already in preview) so it reflects the latest
	// saved bindings.
	useEffect(() => {
		if (viewMode === 'preview' && selectedInvestorId) {
			previewMutation.mutate(selectedInvestorId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewMode, selectedInvestorId, templateId]);

	const previewFileId = previewMutation.data?.fileId ?? null;

	const { data: previewFileResponse, isFetching: isLoadingPreviewFile } = useQuery({
		queryKey: [API_PATHS.FILE_ID_GET, { id: previewFileId }, 'field-binding-preview'],
		queryFn: async ({ signal }) => {
			if (!previewFileId) {
				return undefined;
			}
			const response = await getFileReactQuery({ id: previewFileId }, signal);
			return response ? await response.blob() : undefined;
		},
		enabled: !!previewFileId,
	});

	const selectedField = bindings.find((row) => row.fieldKey === selectedFieldKey) ?? null;

	const progressLabel = tMipManage('master_documents.field_binding.progress', {
		bound: boundCount,
		total: totalCount,
	});

	const isPreviewMode = viewMode === 'preview';
	const isPreviewLoading = previewMutation.isPending || isLoadingPreviewFile;
	const previewUnavailable = selectedInvestorId == null;

	if (isLoading) {
		return (
			<Text size="sm" c="gray.7">
				{tMipManage('master_documents.field_binding.loading')}
			</Text>
		);
	}

	return (
		<Flex w="100%" h="100%" direction="column" gap={8} mih={0}>
			{/* 3-col grid: empty spacer | centred Original/Preview toggle | right cell,
			    holding the "Preview as" pill at deal level or the caller's headerAction
			    at global level. The spacer balances it so the toggle stays centred. */}
			<ToggleRow>
				<span />
				<Tooltip
					label={tMipManage('master_documents.field_binding.preview_tooltip')}
					disabled={!previewUnavailable}
				>
					{/* span wrapper: a disabled control doesn't fire the pointer
					    events Mantine's Tooltip listens for, so hover state
					    lives on this wrapper instead. */}
					<span>
						<SegmentedControl
							data-testid="field-binding-view-toggle"
							value={viewMode}
							onChange={(value) => setViewMode(value as ViewMode)}
							disabled={previewUnavailable}
							data={[
								{
									label: tMipManage(
										'master_documents.field_binding.view.original'
									),
									value: 'original',
								},
								{
									label: tMipManage(
										'master_documents.field_binding.view.preview'
									),
									value: 'preview',
								},
							]}
						/>
					</span>
				</Tooltip>
				{showInvestorPreview ? (
					<PreviewAsSelect
						options={investorOptions}
						value={selectedInvestorId}
						onChange={handlePreviewInvestorChange}
						onDropdownOpen={onInvestorDropdownOpen}
						isFetching={isFetchingInvestors}
						disabled={isSaving}
						shortcutsActive={isPreviewMode}
					/>
				) : (
					<HeaderActionSlot>{headerAction}</HeaderActionSlot>
				)}
			</ToggleRow>
			<DocumentViewerHeader
				title={
					<HeaderContent>
						<Text fw={600}>{progressLabel}</Text>
						<Legend>
							<LegendItem>
								<LegendSwatch $variant="unbound" />
								<Text size="xs" c="dimmed">
									{tMipManage(
										'master_documents.field_binding.legend.needs_binding'
									)}
								</Text>
							</LegendItem>
							<LegendItem>
								<LegendSwatch $variant="bound" />
								<Text size="xs" c="dimmed">
									{tMipManage('master_documents.field_binding.legend.bound')}
								</Text>
							</LegendItem>
						</Legend>
					</HeaderContent>
				}
				onUpscale={onUpscale}
				onDownscale={onDownscale}
				onBackArrowClick={onClose}
				showBackButton
			/>
			<Split $singleColumn={isPreviewMode}>
				<CanvasPane>
					<EditorCanvasPane
						isPreviewMode={isPreviewMode}
						isPreviewLoading={isPreviewLoading}
						previewFileResponse={previewFileResponse}
						file={file}
						bindings={bindings}
						selectedFieldKey={selectedFieldKey}
						setSelectedFieldKey={setSelectedFieldKey}
						scale={scale}
						onBoxChange={onBoxChange}
						onAddBox={onAddBox}
						onRemove={handleRemove}
					/>
				</CanvasPane>
				{/* Hidden in preview mode: the preview is a real render of the SAVED bindings, so an
				    edit made against it reports success and changes nothing on the page in front of
				    the author. Read-only until they switch back. */}
				{!isPreviewMode && (
					<SidePanel data-testid="field-binding-side-panel">
						{panelHeader}
						{paletteTabs}
						<BindPanelSlot>
							{selectedField ? (
								<BindPanel
									key={selectedField.fieldKey}
									field={selectedField}
									catalogue={catalogue}
									securities={securities}
									scopedSamples={scopedSamples}
									uncollectedRequirements={uncollectedRequirements}
									onChangeBinding={onChangeBinding}
									onApplySecurityToAll={onApplySecurityToAll}
									onRenameField={onRenameField}
									onRemove={() => handleRemove(selectedField.fieldKey)}
									onClose={() => setSelectedFieldKey(null)}
								/>
							) : (
								<BindPanelEmptyState
									variant={totalCount === 0 ? 'no-fields' : 'none-selected'}
									unboundCount={totalCount - boundCount}
								/>
							)}
						</BindPanelSlot>
						<BindPanelFooter
							isDirty={isPanelDirty}
							isSaving={isSaving || !!panelHeaderEdit?.isSaving}
							onSave={() => void handlePanelSave()}
							onDiscard={handlePanelDiscard}
						/>
					</SidePanel>
				)}
			</Split>
		</Flex>
	);
};
